import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  DollarSign,
  TrendingUp,
  Smartphone,
  FileText,
  User,
  LogOut,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { authService } from '../../services/authService';

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const account = authService.getCurrentAccount();

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
    // Close sidebar after logout
    if (window.innerWidth < 1024) {
      onToggle();
    }
  };

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/dashboard',
    },
    {
      icon: DollarSign,
      label: 'Revenue',
      path: '/revenue',
    },
    {
      icon: TrendingUp,
      label: 'Performance',
      path: '/performance',
    },
    {
      icon: Smartphone,
      label: 'Apps',
      path: '/apps',
    },
    {
      icon: FileText,
      label: 'Reports',
      path: '/reports',
    },
    // Finance link hidden until backend/frontend deployment is finalized
    // {
    //   icon: CreditCard,
    //   label: 'Finance',
    //   path: '/finance/invoices',
    // },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar: full viewport height on mobile (100dvh for iOS Safari), normal flow on desktop */}
      <aside
        className={cn(
          'w-64 bg-background border-r border-border flex flex-col flex-shrink-0',
          'lg:relative lg:translate-x-0 lg:h-full lg:min-h-0',
          'fixed left-0 z-50 transform transition-transform duration-300 ease-in-out',
          'top-0 bottom-0 h-[100dvh] min-h-[100vh]', // full visible height on mobile (dvh = iOS Safari dynamic viewport)
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar Header (Mobile Only) */}
        <div className="lg:hidden flex items-center h-16 px-3 sm:px-4 border-b border-border bg-background flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="flex-shrink-0 -ml-1"
          >
            <X className="w-5 h-5" />
          </Button>
          <Link
            to="/dashboard"
            className="text-base sm:text-lg font-semibold text-foreground hover:text-primary flex-1 min-w-0"
            onClick={() => {
              if (window.innerWidth < 1024) {
                onToggle();
              }
            }}
          >
            <span className="truncate block">IronSource Dashboard</span>
          </Link>
        </div>

        {/* Navigation - Scrollable middle section */}
        <nav className="flex-1 px-2 py-2 space-y-1 overflow-y-auto overflow-x-hidden min-h-0">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  // Close mobile menu on navigation
                  if (window.innerWidth < 1024) {
                    onToggle();
                  }
                }}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-accent'
                )}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Account & Actions Section - safe-area for iPhone notch/home indicator */}
        {user && (
          <div className="px-2 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-border space-y-2 flex-shrink-0">
            {/* Account Info */}
            <div className="px-3 py-2">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {user.email}
                  </div>
                  {account && (
                    <div className="text-xs text-muted-foreground truncate">
                      {account.email}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full justify-start"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
